import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';
import { setSession } from './utils';

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }
  return state;
};

export const AuthContext = createContext(null);

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const storageAvailable = localStorageAvailable();

  const initialize = useCallback(async () => {
    try {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';

      if (accessToken) {
        setSession(accessToken);

        const res = await axios.get('/auth/me');
        const user = res?.data?.data;

        if (user) {
          dispatch({
            type: 'INITIAL',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
          return;
        }
      }

      setSession(null);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    } catch (error) {
      console.error('Auth initialize error:', error);
      setSession(null);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, [storageAvailable]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const login = useCallback(async (email, password) => {
    try {
      const payload = { email, password };
      const response = await axios.post('/auth/login', payload);
      const { token } = response?.data?.data;

      setSession(token);

      const meRes = await axios.get('/auth/me');
      const user = meRes?.data?.data;

      dispatch({
        type: 'LOGIN',
        payload: { user },
      });

      return {
        status: 'success',
        message: response?.data?.message || 'Login successful',
        data: user,
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        status: 'error',
        message:
          error?.response?.data?.message ||
          'البريد الالكتروني او كلمة المرور غير صحيحة',
      };
    }
  }, []);

  const logout = useCallback(async () => {
    const res = await axios.post('/auth/logout');
    setSession(null);
    dispatch({ type: 'LOGOUT' });
    return res;
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      login,
      logout,
    }),
    [state.isAuthenticated, state.isInitialized, state.user, login, logout]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
